import { injectable } from 'inversify-props';
import { forEach } from 'lodash';
import dayjs from '@/plugins/dayjs';
import BaseService from '@/services/base-service';
import ClientModel from '@/models/crm/client.model';
import { IKeyValue } from '@/interfaces/key-value.interface';
import { ClientTypeEnum } from '@/enums/client-type.enum';
import { RequestConfig } from '@/interfaces/request-config.interface';
import ProspectModel from '@/models/crm/prospect.model';
import GroupedSalesForceInfosModel from '@/models/b2sp/grouped-sales-force-infos.model';
import IntegrationFieldValueModel from '@/models/crm/integration-field-value.model';
import { ClientCnpjHelper } from '@/utils/helpers/client-cnpj-helper';
// import ContactModel from '@/models/crm/contact.model';

// Controle de requisições simultâneas para /crm/general-data.
// Chave: "clientId:type" — impede múltiplas requests para o mesmo cliente ao mesmo tempo
// sem cache persistente (cada nova chamada fora do inflight vai ao backend normalmente).
const generalDataInflight = new Map<string, Promise<ClientModel>>();

// CNPJs agrupados lidos do general-data quando o activeClient ainda não trazia a lista (ver getClientCnpjs).
const cnpjsAgrupadosCache = new Map<string, string[]>();

@injectable()
export default class ClientService extends BaseService {
  async quickSearch(keyword: string, type: ClientTypeEnum): Promise<ClientModel[]> {
    const config: RequestConfig = {
      params: {
        term: keyword.toString(),
        type,
      },
    };

    if (type === ClientTypeEnum.Prospect) {
      const result = (await this.httpService.get(
        ProspectModel,
        `${this.getApiPath()}/crm/quick-search`,
        config,
      )) as ProspectModel[];
      return result.map((x) => x.clientModel);
    }

    return (await this.httpService.get(ClientModel, `${this.getApiPath()}/crm/quick-search`, config)) as ClientModel[];
  }

  async advancedSearch(params: IKeyValue<string | null>, type: ClientTypeEnum): Promise<ClientModel[]> {
    const queryParams: string[] = [];

    forEach(params, (value: string | null, key: string) => {
      if (value) {
        queryParams.push(`${key}=${encodeURIComponent(value)}`);
      }
    });

    if (type === ClientTypeEnum.Prospect) {
      const result = (await this.httpService.get(
        ProspectModel,
        `${this.getApiPath()}/crm/advanced-search?type=${type}&${queryParams.join('&')}`,
      )) as ProspectModel[];

      return result.map((x) => x.clientModel);
    }

    return (await this.httpService.get(
      ClientModel,
      `${this.getApiPath()}/crm/advanced-search?type=${type}&${queryParams.join('&')}`,
    )) as ClientModel[];
  }

  async getClientContacts(clientCnpj: string): Promise<Array<string>> {
    const config: RequestConfig = {
      params: {
        clientCnpj,
      },
    };

    return (await this.httpService.get(String, `${this.getApiPath()}/crm/client-contacts`, config)
    ) as Array<string>;
  }

  async get(clientId: string, type: ClientTypeEnum): Promise<ClientModel> {
    const inflightKey = `${clientId.replace(/[^0-9A-Za-z]/g, '')}:${type}`;

    if (generalDataInflight.has(inflightKey)) {
      return generalDataInflight.get(inflightKey) as Promise<ClientModel>;
    }

    const config: RequestConfig = {
      params: {
        clientId: clientId.replace(/[^0-9A-Za-z]/g, ''),
        type,
      },
    };

    let request: Promise<ClientModel>;

    if (type === ClientTypeEnum.Prospect) {
      request = (this.httpService.get(
        ProspectModel, `${this.getApiPath()}/crm/general-data`, config,
      ) as Promise<ProspectModel>).then((result) => result.clientModel)
        .finally(() => generalDataInflight.delete(inflightKey));
    } else {
      request = (this.httpService.get(
        ClientModel, `${this.getApiPath()}/crm/general-data`, config,
      ) as Promise<ClientModel>).finally(() => generalDataInflight.delete(inflightKey));
    }

    generalDataInflight.set(inflightKey, request);
    return request;
  }

  async quickSearchIgsClient(keyword: string): Promise<ClientModel[]> {
    return (await this.httpService.get(
      ClientModel,
      `${this.getApiPath()}/crm/quick-search-igs-client?term=${encodeURIComponent(keyword).toString()}`,
    )) as ClientModel[];
  }

  async getIgsClient(clientId: string): Promise<ClientModel> {
    return (await this.httpService.get(
      ClientModel,
      `${this.getApiPath()}/crm/igs-client?cnpj=${clientId.replace(/[^0-9A-Za-z]/g, '')}`,
    )) as ClientModel;
  }

  // CNPJ do cliente + CNPJs agrupados a ele no ERP. O activeClient já traz a lista quando veio do
  // summary-data; se veio da busca, usa o general-data (o painel já o consulta ao abrir, e o inflight
  // evita requisição repetida), guarda o resultado e o grava no cliente.
  async getClientCnpjs(client: ClientModel): Promise<string[]> {
    if (!client || !client.cnpjCpf) {
      return [];
    }

    let { cnpjsAgrupados } = client;
    if (!cnpjsAgrupados) {
      const key = ClientCnpjHelper.normalize(client.cnpjCpf);
      cnpjsAgrupados = cnpjsAgrupadosCache.get(key);
      if (!cnpjsAgrupados) {
        try {
          cnpjsAgrupados = (await this.get(client.cnpjCpf, ClientTypeEnum.Client)).cnpjsAgrupados || [];
          cnpjsAgrupadosCache.set(key, cnpjsAgrupados);
        } catch {
          return [client.cnpjCpf];
        }
      }
      // eslint-disable-next-line no-param-reassign
      client.cnpjsAgrupados = cnpjsAgrupados;
    }

    const cnpjs = [client.cnpjCpf];
    (cnpjsAgrupados || []).forEach((cnpj) => {
      if (!cnpjs.some((item) => ClientCnpjHelper.isSame(item, cnpj))) {
        cnpjs.push(cnpj);
      }
    });
    return cnpjs;
  }

  async getSummary(clientId: string, type: ClientTypeEnum, prospectForm = false): Promise<ClientModel> {
    const config: RequestConfig = {
      params: {
        clientId,
        type,
        prospectForm,
      },
    };

    if (type === ClientTypeEnum.Prospect) {
      const result = (await this.httpService.get(
        ProspectModel,
        `${this.getApiPath()}/crm/summary-data`,
        config,
      )) as ProspectModel;
      return result.clientModel;
    }

    return (await this.httpService.get(ClientModel, `${this.getApiPath()}/crm/summary-data`, config)) as ClientModel;
  }

  async saveParticularities(
    cnpjCpf: string | null,
    prospectId: string | null,
    clientType: string,
    particularities: string,
  ): Promise<void> {
    const config: RequestConfig = {
      params: {
        cnpjCpf,
        prospectId,
        clientType,
        particularities,
      },
    };
    await this.httpService.post(
      ClientModel,
      `${this.getApiPath()}/crm/save-particularities`, {}, config,
    );
  }

  async getSalesForceInfos(cnpjCpf: string | null): Promise<GroupedSalesForceInfosModel[]> {
    const config: RequestConfig = {
      params: {
        cnpjCpf,
      },
    };
    const teste = await this.httpService.get(
      GroupedSalesForceInfosModel,
      `${this.getApiPath()}/crm/campos-customizados-fv`,
      config,
    ) as GroupedSalesForceInfosModel[];

    return teste;
  }

  static generateClientExportFilename(date: Date): string {
    return `Empresa_Clientes_${dayjs(date).format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
  }

  static generateProspectExportFilename(date: Date): string {
    return `Empresa_Prospects_${dayjs(date).format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
  }

  async integrateSingleClient(cnpj: string): Promise<string> {
    const config: RequestConfig = {
      params: {
        cnpj,
      },
    };

    return (await this.httpService.post(String, `${this.getApiPath()}/crm/integrate-client`, {}, config)) as string;
  }

  async getProspectFieldDefinitions(): Promise<IntegrationFieldValueModel[]> {
    return (await this.httpService.get(
      IntegrationFieldValueModel,
      `${this.getApiPath()}/prospect/campos-integracao`,
    )) as IntegrationFieldValueModel[];
  }

  async getClientFields(cnpj?: string): Promise<IntegrationFieldValueModel[]> {
    const config: RequestConfig = {
      params: cnpj ? { cnpj: cnpj.replace(/\D/g, '') } : {},
    };
    return (await this.httpService.get(
      IntegrationFieldValueModel,
      `${this.getApiPath()}/crm/cliente-campos-integracao`,
      config,
    )) as IntegrationFieldValueModel[];
  }

  async upsertClientFields(
    cnpj: string,
    entries: Array<Partial<IntegrationFieldValueModel>>,
  ): Promise<IntegrationFieldValueModel[]> {
    const config: RequestConfig = {
      params: { cnpj: cnpj.replace(/\D/g, '') },
    };
    return (await this.httpService.post(
      IntegrationFieldValueModel,
      `${this.getApiPath()}/crm/cliente-campos-integracao`,
      entries,
      config,
    )) as IntegrationFieldValueModel[];
  }
}
